import React from "react";

interface ErrorBoundaryProps {
    children: React.ReactNode
}

interface ErrorBoundaryState {
    hasError: boolean
    message: string
}

/**
 * Top-level error boundary. Before this existed, any render exception
 * unmounted the whole React tree and left a completely blank page with
 * no way to recover other than reloading. Now the user gets an error
 * message and a Reload button.
 *
 * The fallback intentionally uses plain HTML (no Mantine/theme context)
 * so it renders even if the crash came from a provider or the theme.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {hasError: false, message: ""};
    }

    static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
        return {
            hasError: true,
            message: error instanceof Error ? error.message : String(error),
        };
    }

    componentDidCatch(error: unknown, errorInfo: React.ErrorInfo): void {
        console.error("[ErrorBoundary] Uncaught render error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    height: "100vh",
                    display: "grid",
                    placeItems: "center",
                    fontFamily: "system-ui, sans-serif",
                }}>
                    <div style={{
                        display: "grid",
                        gridTemplateRows: "auto auto auto",
                        rowGap: "1rem",
                        justifyItems: "center",
                        padding: "2rem",
                        maxWidth: "28rem",
                        textAlign: "center",
                    }}>
                        <h2 style={{margin: 0}}>Something went wrong</h2>
                        <p style={{margin: 0}}>
                            Neurotune hit an unexpected error{this.state.message ? `: ${this.state.message}` : "."}
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            data-testid="error-boundary-reload"
                            style={{
                                padding: "0.5rem 1.5rem",
                                borderRadius: "6px",
                                border: "1px solid #888",
                                background: "transparent",
                                cursor: "pointer",
                            }}
                        >
                            Reload
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
